import { calculateDaysRemaining, formatDateString, isDateBeforeToday } from '@/main/helpers'
import { Category } from '@/domain/models/content/category'
import { Shift } from '@/domain/models/content/shift'
import { ContentFormat } from '@/domain/models/content/content-format'
import {
  ScholarshipData,
  ScholarshipDataAPI,
  ScholarshipRenewalItem,
  ScholarshipRenewalItemAPI,
  ScholarshipRenewalListResponse,
  ScholarshipRenewalListResponseAPI
} from './scholarship-service-types'

export const transformScholarshipData = (data: ScholarshipDataAPI): ScholarshipData => ({
  scholarshipMonthlyDuration: data.scholarshipMonthlyDuration,
  courseMonthlyDuration: data.courseMonthlyDuration,
  scholarshipTotal: data.scholarshipTotal,
  scholarshipAvailable: data.scholarshipAvailable,
  scholarships: data.scholarships.map(item => ({
    uniqueId: item.uniqueId,
    authorizationId: item.authorizationId,
    expiresAt: formatDateString(item.expiresAt),
    startDate: formatDateString(item.startDate),
    endDate: formatDateString(item.endDate),
    confirmationStart: formatDateString(item.confirmationStart),
    confirmationEnd: formatDateString(item.confirmationEnd),
    confirmationEndInDays: calculateDaysRemaining(item.confirmationEnd),
    inConfirmationPeriod: item.inConfirmationPeriod,
    expired: isDateBeforeToday(item.expiresAt),
    status: item.status
  }))
})

// ============================================================================
// Backoffice — Renovação de Bolsa (RH)
// ----------------------------------------------------------------------------
const formatUnitAddress = (unit: ScholarshipRenewalItemAPI['unit']): string => {
  if (!unit?.address) return unit?.name ?? ''

  const { location, complement, neighborhood, city, state } = unit.address
  const street = [location, complement, neighborhood].filter(Boolean).join(', ')
  const region = [city, state].filter(Boolean).join('/')

  return [street, region].filter(Boolean).join(' - ')
}

const formatScholarshipRenewalItem = (item: ScholarshipRenewalItemAPI): ScholarshipRenewalItem => ({
  uniqueId: item.uniqueId,
  authorizationId: item.authorizationId,
  userName: item.userName,
  userSocialName: item.userSocialName ?? undefined,
  document: item.document,
  email: item.email,
  phone: item.phone,
  expiresInDays: calculateDaysRemaining(item.expiresAt),
  status: item.status,
  course: {
    name: item.courseName,
    institutionName: item.institutionName ?? '',
    category: Category.getByName(item.category)?.title ?? item.category,
    format: ContentFormat.getByName(item.format)?.title ?? item.format,
    unit: formatUnitAddress(item.unit),
    shift: Shift.getByName(item.shift)?.title ?? item.shift
  }
})

export const formatScholarshipRenewalList = (
  data?: ScholarshipRenewalListResponseAPI
): ScholarshipRenewalListResponse => {
  const items = data?.items ?? []

  return {
    results: items.map(item => formatScholarshipRenewalItem(item)),
    page: data?.page ?? 1,
    pageSize: data?.pageSize ?? 50,
    resultsQuantity: data?.total ?? items.length
  }
}
